'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Skeleton } from '@common/components';
import SvgBookmarkFill from '@common/icon/SvgBookmarkFill';
import SvgChatbubbleTypingFill from '@common/icon/SvgChatbubbleTypingFill';
import { assert, cn } from '@core/utils';
import dayjs from 'dayjs';
import { BOOKMARK_TYPE } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES, SELECTOR } from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { CONTENT_CATEGORY, GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { extractDate } from '@/utils/extractDate';
export type PolicyCardProps = {
  isLoading?: boolean;
  id?: number;
  idIdx?: string;
  imageUrl?: string;
  name?: string;
  endAt?: string;
  dday?: string;
  benefit?: string;
  commentCount?: number;
  border?: boolean;
  isBookmarked?: boolean;
  bookmarkCount?: number;
  agencyId?: number;
  agency?: string;
  variant?: 'default' | 'scrap' | 'hometown';
  /** 클릭 이벤트가 있을 때에는 기본 기능인 링크 이동을 방지 */
  onClick?: () => void;
};
const BOOKMARK = '스크랩';
const COMMENT = '댓글';
export const PolicyCard = t0 => {
  const $ = _c(46);
  const {
    endAt,
    name,
    dday,
    benefit,
    bookmarkCount,
    commentCount,
    isBookmarked: t1,
    isLoading,
    onClick,
    idIdx,
    border,
    imageUrl,
    id,
    agency,
    variant: t2
  } = t0;
  const isBookmarked = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const router = useCustomRouter();
  const date = endAt ? extractDate(endAt) : undefined;
  let t3;
  if ($[0] !== idIdx) {
    t3 = ROUTES.POLICY_DETAIL.pathname({
      idIdx: idIdx ?? ""
    });
    $[0] = idIdx;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const href = t3;
  const {
    bookmark
  } = useBookmark();
  const isScrap = variant === "scrap";
  const isHometown = variant === "hometown";
  const remainingDays = date ? dayjs(date).endOf("day").diff(dayjs(), "day") : undefined;
  const t4 = isScrap ? "gap-16 px-20 py-16" : "gap-12 rounded-small bg-Gray-white px-15 pb-14 pt-16";
  const t5 = !isScrap && border && "border border-Gray-300";
  const t6 = isHometown && "p-24";
  let t7;
  if ($[2] !== t4 || $[3] !== t5 || $[4] !== t6) {
    t7 = cn("flex flex-col transition-opacity duration-200 ease-out", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-B-500", t4, t5, t6);
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== id || $[7] !== isLoading || $[8] !== name || $[9] !== onClick) {
    t8 = e => {
      trackGtmEvent(GTM_EVENT_TYPE.SELECT_CONTENT, {
        content_name: name ?? "",
        content_category: CONTENT_CATEGORY.POLICY,
        content_id: id?.toString() ?? ""
      });
      if (isLoading || onClick) {
        e.preventDefault();
      }
      onClick?.();
    };
    $[6] = id;
    $[7] = isLoading;
    $[8] = name;
    $[9] = onClick;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== benefit || $[12] !== isHometown || $[13] !== name || $[14] !== remainingDays) {
    t9 = isHometown ? <><h4 className="line-clamp-3 break-keep font-700 text-Gray-black typo-body3">{name}</h4><div className="flex items-center gap-4">{benefit ? <Badge dimensions="H20" className="bg-Gray-50 text-Gray-700">{benefit}</Badge> : null}{remainingDays !== undefined && remainingDays >= 0 ? <Badge dimensions="H20" className="bg-G-50 text-G-500">{`D-${remainingDays}`}</Badge> : null}</div></> : null;
    $[11] = benefit;
    $[12] = isHometown;
    $[13] = name;
    $[14] = remainingDays;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== dday || $[17] !== imageUrl || $[18] !== isHometown || $[19] !== isLoading || $[20] !== isScrap || $[21] !== name) {
    t10 = isHometown ? null : <section className="flex items-center gap-12">{isLoading ? <Skeleton className="size-52 rounded-medium" /> : <Image alt={name || "\uC815\uCC45 \uC774\uBBF8\uC9C0"} className={cn("size-52 rounded-large border border-Gray-300", isScrap ? "object-contain" : "object-cover p-6")} height={52} src={imageUrl} width={52} />}{isLoading ? <Skeleton className="w-80" typo="body3" /> : <div className="line-clamp-2">{dday && !isScrap ? <Badge className="mr-8 inline-block h-23 text-R-400">{dday}</Badge> : null}<h4 className="inline break-keep font-500 typo-body3">{name}</h4></div>}</section>;
    $[16] = dday;
    $[17] = imageUrl;
    $[18] = isHometown;
    $[19] = isLoading;
    $[20] = isScrap;
    $[21] = name;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== agency || $[24] !== benefit || $[25] !== bookmark || $[26] !== bookmarkCount || $[27] !== commentCount || $[28] !== date || $[29] !== endAt || $[30] !== href || $[31] !== id || $[32] !== idIdx || $[33] !== isBookmarked || $[34] !== isHometown || $[35] !== isLoading || $[36] !== name || $[37] !== router) {
    t11 = isHometown ? null : isLoading ? <div className="flex justify-between gap-16"><div className="flex flex-1 items-center gap-10"><Skeleton className="w-100" typo="caption1" /><Skeleton className="w-80" typo="caption1" /></div><Skeleton className="w-28" typo="caption1" /><Skeleton className="w-28" typo="caption1" /></div> : <footer className="flex justify-between gap-8">{isLoading ? <div className="flex flex-1 items-center gap-10"><Skeleton className="w-100" typo="caption1" /><Skeleton className="w-80" typo="caption1" /></div> : <div className="flex flex-1 items-center gap-5 overflow-hidden text-nowrap text-Gray-600 typo-caption1">{benefit ? <p className="truncate">{benefit}</p> : null}{benefit && endAt ? <div className="h-10 w-1 bg-Gray-400" /> : null}{endAt ? <time dateTime={date}>{endAt}</time> : null}</div>}{typeof commentCount === "number" ? <button aria-label={COMMENT} className={cn("-m-8 gap-4 p-8 text-Gray-700 transition-opacity duration-200 flex-center typo-caption1", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-B-500")} title={COMMENT} type="button" onClick={e_0 => {
        e_0.preventDefault();
        e_0.stopPropagation();
        router.push(href + "#" + SELECTOR.COMMENT_LIST_TOP);
      }}><SvgChatbubbleTypingFill aria-label={COMMENT} className="size-16 text-Gray-400" />{commentCount < 1000 ? commentCount : "999+"}</button> : null}{typeof bookmarkCount === "number" ? <button aria-label={BOOKMARK} className={cn("-m-8 gap-4 p-8 text-Gray-700 transition-opacity duration-200 flex-center typo-caption1", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-B-500")} title={BOOKMARK} type="button" onClick={e_1 => {
        e_1.preventDefault();
        e_1.stopPropagation();
        assert(id);
        bookmark({
          bookmarkType: BOOKMARK_TYPE.POLICY,
          contentId: id,
          isBookmarked,
          idx: idIdx,
          contentName: name,
          policyAgency: agency
        });
      }}><SvgBookmarkFill aria-label={BOOKMARK} className={cn("size-16 text-Gray-400 transition-colors duration-75", isBookmarked && "text-B-500")} />{bookmarkCount < 1000 ? bookmarkCount : "999+"}</button> : null}</footer>;
    $[23] = agency;
    $[24] = benefit;
    $[25] = bookmark;
    $[26] = bookmarkCount;
    $[27] = commentCount;
    $[28] = date;
    $[29] = endAt;
    $[30] = href;
    $[31] = id;
    $[32] = idIdx;
    $[33] = isBookmarked;
    $[34] = isHometown;
    $[35] = isLoading;
    $[36] = name;
    $[37] = router;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== href || $[40] !== t10 || $[41] !== t11 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t12 = <CustomLink className={t7} href={href} onClick={t8}>{t9}{t10}{t11}</CustomLink>;
    $[39] = href;
    $[40] = t10;
    $[41] = t11;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  return t12;
};